import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { ExternalLink, Play } from 'lucide-react';
import Button from './Button';
import {
  SPORTS_FUTURE_PLAYLIST_ID,
  fetchPlaylistVideos,
  youtubeEmbedPlaylistUrl,
  youtubePlaylistUrl,
  youtubeWatchUrl,
  type PlaylistVideo,
} from '../utils/youtubePlaylist';

const PLAYLIST_ID = SPORTS_FUTURE_PLAYLIST_ID;

const VideoFeed = () => {
  const [videos, setVideos] = useState<PlaylistVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVideos = async () => {
    try {
      const items = await fetchPlaylistVideos(PLAYLIST_ID, 8);
      setVideos(items.filter((video) => video.id));
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching videos:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch videos');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
    const interval = setInterval(fetchVideos, 6 * 60 * 60 * 1000); // Refresh every 6 hours
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (error || videos.length === 0) {
    return (
      <div className="space-y-6">
        <div className="aspect-video w-full overflow-hidden rounded-lg shadow-md">
          <iframe
            src={youtubeEmbedPlaylistUrl(PLAYLIST_ID)}
            title="SportsFuture playlist"
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="text-center">
          <Button as="a" href={youtubePlaylistUrl(PLAYLIST_ID)} target="_blank" rel="noopener noreferrer" variant="outline">
            View All Videos
            <ExternalLink size={16} className="ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {videos.map((video, index) => (
          <motion.div
            key={video.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-md overflow-hidden"
          >
            <a
              href={youtubeWatchUrl(video.id, PLAYLIST_ID)}
              target="_blank"
              rel="noopener noreferrer"
              className="block relative aspect-video group"
            >
              {video.thumbnail ? (
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to a placeholder if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <Play size={48} className="text-gray-400" />
                </div>
              )}
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Play size={48} className="text-white" />
              </div>
            </a>
            <div className="p-5">
              <h3 className="text-xl font-semibold mb-2 line-clamp-2">{video.title}</h3>
              <p className="text-sm text-gray-500 mb-2">
                {formatDistanceToNow(new Date(video.publishedAt), { addSuffix: true })}
              </p>
              <p className="text-gray-600 mb-4 line-clamp-2">{video.description}</p>
              <a
                href={youtubeWatchUrl(video.id, PLAYLIST_ID)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-medium hover:underline inline-flex items-center"
              >
                Watch on YouTube
                <ExternalLink size={16} className="ml-1" />
              </a>
            </div>
          </motion.div>
        ))}
      </div>
      
      <div className="text-center">
        <Button
          as="a"
          href={youtubePlaylistUrl(PLAYLIST_ID)}
          target="_blank"
          rel="noopener noreferrer"
          variant="outline"
        >
          View All Videos
          <ExternalLink size={16} className="ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default VideoFeed;